import { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Send, Users, Copy, CheckCircle2 } from "lucide-react";

export default function InviteTeammateModal({ open, onClose, domains, onInvited }) {
  const verified = (domains || []).filter(d => d.verified);
  const [email, setEmail] = useState("");
  const [alias, setAlias] = useState("");
  const [domainId, setDomainId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setEmail(""); setAlias(""); setNote(""); setResult(null);
      setDomainId(verified[0]?.id || "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!domainId) return toast.error("Pick a verified domain");
    setSubmitting(true);
    try {
      const { data } = await api.post("/invites", {
        email: email.trim().toLowerCase(),
        alias: alias.trim().toLowerCase(),
        domain_id: domainId,
        note: note.trim() || null,
      });
      setResult(data);
      if (onInvited) onInvited();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.accept_url); toast.success("Link copied"); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto" data-testid="invite-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
          <div>
            <h2 className="text-xl font-bold text-[#0F172A] flex items-center gap-2"><Users className="h-5 w-5 text-[#F26953]"/> Invite a teammate</h2>
            <p className="text-xs text-[#475569] mt-1">They'll get a magic link. Accepting creates their alias forwarded to their inbox.</p>
          </div>
          <button onClick={onClose} data-testid="invite-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5"/>
          </button>
        </div>

        {!result ? (
          <form onSubmit={onSubmit} className="p-6 space-y-4" data-testid="invite-form">
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Teammate's email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="invite-email" placeholder="alex@example.com"
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Alias to give them</span>
              <div className="mt-1 flex rounded-lg border border-[#E2E8F0] overflow-hidden focus-within:ring-2 focus-within:ring-[#F26953]/20 focus-within:border-[#F26953]">
                <input required value={alias} onChange={(e) => setAlias(e.target.value.toLowerCase())}
                  pattern="[a-z0-9._-]+" data-testid="invite-alias" placeholder="alex"
                  className="flex-1 px-3 py-2.5 outline-none text-sm mono"/>
                <select value={domainId} onChange={(e) => setDomainId(e.target.value)}
                  data-testid="invite-domain" className="bg-slate-50 border-l border-[#E2E8F0] px-3 py-2.5 outline-none text-sm mono text-[#475569]">
                  {verified.map(d => <option key={d.id} value={d.id}>@{d.domain}</option>)}
                </select>
              </div>
              {verified.length === 0 && (
                <div className="text-xs text-amber-600 mt-2">You need at least one verified domain to invite teammates.</div>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Personal note <span className="text-slate-400 font-normal">(optional)</span></span>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                data-testid="invite-note" placeholder="Hey, setting you up with your @ourdomain email — accept to activate."
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
            </label>
            <button type="submit" disabled={submitting || verified.length === 0} data-testid="invite-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold py-2.5 rounded-lg hover:bg-[#D85340] disabled:opacity-60">
              {submitting ? "Sending…" : <>Send invite <Send className="h-4 w-4"/></>}
            </button>
          </form>
        ) : (
          <div className="p-6 space-y-4" data-testid="invite-success">
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5"/>
              <div>
                <div className="text-sm font-semibold text-emerald-900">Invite sent to {email}</div>
                <div className="text-xs text-emerald-800/80 mt-1">They'll get an email with a link to activate <span className="mono">{alias}@{verified.find(d => d.id === domainId)?.domain}</span>.</div>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Or share the accept link directly</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 text-xs bg-slate-50 border border-[#E2E8F0] rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap">{result.accept_url}</code>
                <button onClick={copyLink} data-testid="invite-copy-link" className="px-3 rounded-lg border border-[#E2E8F0] bg-white hover:bg-slate-50 text-slate-500">
                  <Copy className="h-4 w-4"/>
                </button>
              </div>
            </div>
            <button onClick={onClose} data-testid="invite-done"
              className="w-full bg-[#0F172A] text-white font-semibold py-2.5 rounded-lg hover:bg-slate-800">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
